import React, { useState, useMemo, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Chip,
  Stack,
  Grid,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import {
  DistributorCard,
  DistributorsTable,
  DistributorStats,
  DistributorDashboard,
  DistributorTemplates
} from '../components/distributors';
import SearchBar from '../components/common/SearchBar';
import DistributorFormModal from '../components/forms/DistributorFormModal';
import { useDistributors, useSearchAndFilter } from '../hooks';
import usePageOperations from '../hooks/usePageOperations';
import { THEME_COLORS, BREAKPOINTS } from '../constants';

/**
 * Página de gestión de distribuidores
 */
const DistributorsPageComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.MOBILE));

  const distributorsHook = useDistributors();
  const {
    distributors,
    stats,
    loading,
    error,
    pagination,
    goToPage,
    changePageSize,
    searchDistributors,
    toggleDistributorStatus
  } = distributorsHook;

  const {
    openCreateModal,
    openEditModal,
    openDeleteModal,
    FormModal,
    DeleteModal,
    OperationModal,
  } = usePageOperations({
    entityName: 'Distribuidor',
    entityNamePlural: 'Distribuidores',
    crudFunctions: {
      create: distributorsHook.createDistributor,
      update: distributorsHook.updateDistributor,
      delete: distributorsHook.deleteDistributor,
    },
    FormModalComponent: DistributorFormModal,
    entityIdKey: 'distributor_id',
  });

  const {
    searchInput,
    setSearchInput,
    searchTerm,
    filterStatus,
    handleSearch,
    handleClearSearch,
    handleKeyPress,
    handleFilterChange,
  } = useSearchAndFilter();

  const [expandedDistributorId, setExpandedDistributorId] = useState(null);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);

  // Filtrar distribuidores basado en búsqueda y filtros
  const filteredDistributors = useMemo(() => {
    let filtered = distributors;

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(distributor =>
        distributor.nombre_empresa.toLowerCase().includes(searchTerm.toLowerCase()) ||
        distributor.nombre_distribuidor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        distributor.celular.includes(searchTerm) ||
        distributor.cuenta_ewelink.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (distributor.ciudad && distributor.ciudad.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (distributor.estado && distributor.estado.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Aplicar filtro de estado
    if (filterStatus === 'active') {
      filtered = filtered.filter(distributor => distributor.is_active);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(distributor => !distributor.is_active);
    }

    return filtered;
  }, [distributors, searchTerm, filterStatus]);

  const handleExpand = useCallback((distributorId) => {
    setExpandedDistributorId(prev => (prev === distributorId ? null : distributorId));
  }, []);

  const handleViewDashboard = useCallback((distributor) => {
    setSelectedDistributor(distributor);
    setDashboardOpen(true);
  }, []);

  const handleCloseDashboard = useCallback(() => {
    setDashboardOpen(false);
    setSelectedDistributor(null);
  }, []);

  const handleToggleStatus = useCallback(async (distributor) => {
    const newStatus = !distributor.is_active;
    await toggleDistributorStatus(distributor.distributor_id, newStatus, distributor);
  }, [toggleDistributorStatus]);

  // Realizar búsqueda cuando cambie el término de búsqueda
  React.useEffect(() => {
    if (searchTerm !== undefined) {
      searchDistributors(searchTerm, filterStatus === 'active');
    }
  }, [searchTerm, filterStatus, searchDistributors]);

  if (error) {
    return (
      <AppLayout currentPage="distributors">
        <Box sx={{ paddingY: 4 }}>
          <Typography variant="h6" color="error" align="center">
            Error al cargar distribuidores: {error}
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="distributors">
      <Fade in timeout={500}>
        <Box>
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="h4"
              sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
            >
              Gestión de Distribuidores
            </Typography>
            <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: { xs: '0.875rem', sm: '1rem' }, mt: 1 }}>
              Aquí puedes crear, visualizar y administrar todos tus distribuidores.
            </Typography>
          </Box>

          <Grow in timeout={700}>
            <Box>
              <DistributorStats stats={stats} />
            </Box>
          </Grow>

          <Grow in timeout={750}>
            <Box sx={{ mt: 4 }}>
              <DistributorTemplates />
            </Box>
          </Grow>

          <Grow in timeout={800}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                marginTop: 4,
                marginBottom: 4,
                border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                borderRadius: '16px',
                backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <SearchBar
                    searchInput={searchInput}
                    searchTerm={searchTerm}
                    placeholder="Buscar distribuidores por empresa, nombre, celular, email o ubicación..."
                    onSearchInputChange={setSearchInput}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    onKeyPress={handleKeyPress}
                  />
                </Grid>
                <Grid item xs={12} sx={{ pt: 0.5 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-start"
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    <Chip
                      label="Todos"
                      onClick={() => handleFilterChange('all')}
                      clickable
                      variant={filterStatus === 'all' ? 'filled' : 'outlined'}
                      sx={{ borderRadius: '8px', fontWeight: 500 }}
                    />
                    <Chip
                      label="Distribuidores Activos"
                      onClick={() => handleFilterChange('active')}
                      clickable
                      variant={filterStatus === 'active' ? 'filled' : 'outlined'}
                      sx={{ borderRadius: '8px', fontWeight: 500 }}
                    />
                    <Chip
                      label="Distribuidores Inactivos"
                      onClick={() => handleFilterChange('inactive')}
                      clickable
                      variant={filterStatus === 'inactive' ? 'filled' : 'outlined'}
                      sx={{ borderRadius: '8px', fontWeight: 500 }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grow>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateModal}
              sx={{
                backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                color: THEME_COLORS.BACKGROUND_PAPER,
                borderRadius: '12px',
                paddingX: 3,
                paddingY: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a1a1a' },
              }}
            >
              Nuevo Distribuidor
            </Button>
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {filteredDistributors.map(distributor => (
                <DistributorCard
                  key={distributor.distributor_id}
                  distributor={distributor}
                  onEdit={() => openEditModal(distributor)}
                  onDelete={() => openDeleteModal(distributor)}
                  onToggleStatus={() => handleToggleStatus(distributor)}
                  onViewDashboard={() => handleViewDashboard(distributor)}
                  isExpanded={expandedDistributorId === distributor.distributor_id}
                  onExpand={handleExpand}
                />
              ))}
            </Box>
          ) : (
            <DistributorsTable
              distributors={filteredDistributors}
              expandedDistributorId={expandedDistributorId}
              onExpand={handleExpand}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
              onToggleStatus={handleToggleStatus}
              onViewDashboard={handleViewDashboard}
              loading={loading}
              // Props de paginación
              pagination={pagination}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
            />
          )}
        </Box>
      </Fade>

      {/* Modales */}
      <FormModal />
      <DeleteModal />
      <OperationModal />

      {/* Dashboard Modal */}
      <DistributorDashboard
        open={dashboardOpen}
        onClose={handleCloseDashboard}
        distributor={selectedDistributor}
      />
    </AppLayout>
  );
};

// Memoizar el componente para optimización
const DistributorsPage = React.memo(DistributorsPageComponent);
DistributorsPage.displayName = 'DistributorsPage';

export default DistributorsPage;